import { m } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// auth / routes
import { useAuthContext } from 'src/auth/hooks';
import { useRouter } from 'src/routes/hooks';
// api / types
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from 'src/api/server/notification';
import type { INetsuiteNotification } from 'src/types/notification';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';

// ----------------------------------------------------------------------

const getSelectedConfigId = (user: any): string => {
  const selected = user?.configSelected;
  if (!selected) return '';
  if (typeof selected === 'string') return selected;
  return selected.id || selected._id || selected.config || '';
};

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const smUp = useResponsive('up', 'sm');
  const router = useRouter();
  const { user } = useAuthContext();
  const userId = user?.id || user?.idCognito || '';
  const configId = getSelectedConfigId(user);

  const [currentTab, setCurrentTab] = useState<'all' | 'unread' | 'archived'>('all');
  const [notifications, setNotifications] = useState<INetsuiteNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const refreshNotifications = useCallback(async () => {
    if (!userId || !configId) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const response = await getNotifications(userId, configId);
      setNotifications(response?.data || []);
    } catch (error: any) {
      setLoadError(error?.message || error?.error || 'Impossible de charger les notifications');
    } finally {
      setLoading(false);
    }
  }, [configId, userId]);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    const handleCreated = (event: Event) => {
      const notification = (event as CustomEvent<INetsuiteNotification>).detail;
      if (!notification?.id) return;

      setNotifications((current) => [
        notification,
        ...current.filter((item) => item.id !== notification.id),
      ]);
    };

    window.addEventListener('ws:notification-created', handleCreated as EventListener);
    return () => {
      window.removeEventListener('ws:notification-created', handleCreated as EventListener);
    };
  }, []);

  const totalUnRead = notifications.filter((item) => item.status === 'unread').length;

  const tabs = useMemo(
    () => [
      {
        value: 'all' as const,
        label: 'Toutes',
        count: notifications.filter((item) => item.status !== 'archived').length,
      },
      {
        value: 'unread' as const,
        label: 'Non lues',
        count: totalUnRead,
      },
      {
        value: 'archived' as const,
        label: 'Archivées',
        count: notifications.filter((item) => item.status === 'archived').length,
      },
    ],
    [notifications, totalUnRead]
  );

  const visibleNotifications = useMemo(() => {
    if (currentTab === 'unread') {
      return notifications.filter((item) => item.status === 'unread');
    }
    if (currentTab === 'archived') {
      return notifications.filter((item) => item.status === 'archived');
    }
    return notifications.filter((item) => item.status !== 'archived');
  }, [currentTab, notifications]);

  const handleChangeTab = useCallback(
    (_event: React.SyntheticEvent, newValue: 'all' | 'unread' | 'archived') => {
      setCurrentTab(newValue);
    },
    []
  );

  const handleMarkAllAsRead = useCallback(async () => {
    if (!userId || !configId || !totalUnRead) return;

    setNotifications((current) =>
      current.map((notification) =>
        notification.status === 'unread'
          ? { ...notification, status: 'read' as const, readAt: new Date().toISOString() }
          : notification
      )
    );

    try {
      await markAllNotificationsRead(userId, configId);
    } catch {
      refreshNotifications();
    }
  }, [configId, refreshNotifications, totalUnRead, userId]);

  const handleOpenDrawer = useCallback(() => {
    drawer.onTrue();
    refreshNotifications();
  }, [drawer, refreshNotifications]);

  const handleOpenNotification = useCallback(
    async (notification: INetsuiteNotification) => {
      if (notification.status === 'unread' && userId && configId) {
        setNotifications((current) =>
          current.map((item) =>
            item.id === notification.id
              ? { ...item, status: 'read' as const, readAt: new Date().toISOString() }
              : item
          )
        );

        try {
          await markNotificationRead(userId, configId, notification.id);
        } catch {
          refreshNotifications();
        }
      }

      if (notification.actionUrl) {
        drawer.onFalse();
        router.push(notification.actionUrl);
      }
    },
    [configId, drawer, refreshNotifications, router, userId]
  );

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Tout marquer comme lu">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} variant="scrollable">
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'error') ||
                (tab.value === 'archived' && 'default') ||
                'info'
              }
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {visibleNotifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onOpen={handleOpenNotification}
          />
        ))}

        {!loading && !loadError && visibleNotifications.length === 0 && (
          <Stack alignItems="center" sx={{ px: 3, py: 8 }}>
            <Iconify icon="solar:bell-off-bold-duotone" width={40} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Aucune notification
            </Typography>
          </Stack>
        )}

        {loading && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            Chargement…
          </Typography>
        )}

        {!!loadError && (
          <Typography variant="body2" color="error" sx={{ p: 3, textAlign: 'center' }}>
            {loadError}
          </Typography>
        )}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <m.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }} variants={varHover(1.05)}>
        <IconButton
          color={drawer.value ? 'primary' : 'default'}
          onClick={handleOpenDrawer}
          aria-label={`${totalUnRead} notification(s) non lue(s)`}
        >
          <Badge badgeContent={totalUnRead} color="error">
            <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
          </Badge>
        </IconButton>
      </m.div>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead}
        <Divider />
        <Box sx={{ px: 1 }}>{renderTabs}</Box>
        <Divider />
        {renderList}
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large" onClick={refreshNotifications}>
            Actualiser
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
